// Sprint 3 Story 5a: Modulares Widget-Dashboard.
//
// Redesign-Paket 2: Der Seitenkopf ist nicht mehr selbst gebaut, sondern wird
// über `usePageHeader` in die EINE App-Kopfzeile geschrieben; der Inhalt liegt
// in `PageShell` (vorher hatte diese Route GAR KEIN `max-w` und lief auf einem
// 27"-Monitor auseinander — bei 2560px endeten die Karten an vier
// verschiedenen rechten Kanten). Das Raster ist jetzt 12-spaltig und jede
// Kartenreihe füllt es voll aus (siehe `gridSpans`).
//
// 12-Spalten-Grid (gespeicherte Größen sind 2/3/4/6 von 6) mit 4 Widget-Größen
// (small/half/wide/full).
// Layout pro User in user_preferences.dashboard_layout (jsonb) gespeichert,
// leeres Array → DEFAULT_LAYOUT.
// Drag-Drop und Widget-Library kommen in Story 5b.

import { useEffect, useState } from "preact/hooks";
import { Check, GripVertical, LayoutGrid, Pencil, Plus } from "lucide-preact";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import type { DashboardWidget, DashboardWidgetSize } from "@juuplan/shared";
import { SortableWidget } from "../components/widgets/SortableWidget";
import { Layout } from "../components/Layout";
import { Modal } from "../components/Modal";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { PageShell } from "../components/ui/PageShell";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import { useMe } from "../lib/auth";
import { useUserPrefs, setUserPrefs } from "../store/userPrefs";
import { updatePreferences } from "../store/notifications";
import {
  DEFAULT_LAYOUT,
  SIZE_SPANS,
  WIDGET_LABELS,
  ALL_WIDGET_IDS,
  effectiveLayout,
  type WidgetId,
} from "../lib/dashboardLayout";
import { WidgetCard } from "../components/widgets/WidgetCard";
import { MyTasksWidget } from "../components/widgets/MyTasksWidget";
import { MyProjectsWidget } from "../components/widgets/MyProjectsWidget";
import { MyListsWidget } from "../components/widgets/MyListsWidget";
import { OverdueWidget } from "../components/widgets/OverdueWidget";
import { ThisWeekWidget } from "../components/widgets/ThisWeekWidget";
import { TodayWidget } from "../components/widgets/TodayWidget";
import { ActivityWidget } from "../components/widgets/ActivityWidget";
import { MentionsWidget } from "../components/widgets/MentionsWidget";
import { ProjectStatsWidget } from "../components/widgets/ProjectStatsWidget";
import { WhatsNewWidget } from "../components/widgets/WhatsNewWidget";
import { ProgressWidget } from "../components/widgets/ProgressWidget";
import { toastSuccess } from "../lib/toast";

/** Spaltenzahl des Rasters auf md+ (die Größen sind auf 6 gespeichert). */
const GRID_COLS = 12;

/**
 * Spalten-Spans für EINE Reihe pro Zeile — die Antwort auf den sichtbarsten
 * Audit-Befund: die Karten endeten an vier verschiedenen rechten Kanten
 * (2560/1380/1088/795px), weil eine halbe Karte allein in einer Reihe stand
 * („Was ist neu" endete bei 1088px, „Meine Aufgabenlisten" war voll breit mit
 * einer Drittel-Karte darin).
 *
 * Vorgehen: die gespeicherten 6er-Größen werden auf das 12er-Raster verdoppelt
 * (2/3/4/6 → 4/6/8/12), dann werden die Karten in Reihen à 12 Spalten gepackt
 * und der Rest einer nicht ganz gefüllten Reihe wird gleichmäßig auf ihre
 * Karten verteilt. Damit endet JEDE Reihe an derselben rechten Kante.
 *
 * Rein darstellend: die gespeicherte Größe (`widget.size`) bleibt unberührt,
 * der Umschalter im Karten-Menü schreibt weiter denselben Wert.
 */
export function gridSpans(layout: readonly DashboardWidget[]): number[] {
  const spans = layout.map((w) => SIZE_SPANS[w.size] * 2);

  // Rest der Reihe [start, endEx) gleichmäßig auf ihre Karten verteilen.
  const fillRow = (start: number, endEx: number, used: number): void => {
    const count = endEx - start;
    if (count <= 0) return;
    let rest = GRID_COLS - used;
    for (let i = 0; rest > 0; i = (i + 1) % count) {
      spans[start + i] += 1;
      rest -= 1;
    }
  };

  let start = 0;
  let used = 0;
  for (let i = 0; i < spans.length; i++) {
    if (used + spans[i] > GRID_COLS) {
      fillRow(start, i, used);
      start = i;
      used = 0;
    }
    used += spans[i];
  }
  fillRow(start, spans.length, used);
  return spans;
}

function widgetContent(id: string): preact.JSX.Element {
  switch (id as WidgetId) {
    case "my-tasks":
      return <MyTasksWidget />;
    case "my-projects":
      return <MyProjectsWidget />;
    case "my-lists":
      return <MyListsWidget />;
    case "overdue":
      return <OverdueWidget />;
    case "this-week":
      return <ThisWeekWidget />;
    case "today":
      return <TodayWidget />;
    case "activity":
      return <ActivityWidget />;
    case "mentions":
      return <MentionsWidget />;
    case "project-stats":
      return <ProjectStatsWidget />;
    case "whats-new":
      return <WhatsNewWidget />;
    case "progress":
      return <ProgressWidget />;
    default:
      return (
        <p class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Unbekanntes Widget „{id}".
        </p>
      );
  }
}

export function Dashboard(): preact.JSX.Element {
  const me = useMe();
  const prefs = useUserPrefs();
  const [layout, setLayout] = useState<DashboardWidget[]>(
    effectiveLayout(prefs.dashboard_layout)
  );
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  // PointerSensor mit kleiner Aktivierungs-Distanz → ein Klick auf den Griff
  // (ohne Ziehen) blockiert nicht; KeyboardSensor macht das Sortieren per
  // Tastatur zugänglich.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Sync with prefs signal (z.B. nach Speichern aus Settings).
  useEffect(() => {
    setLayout(effectiveLayout(prefs.dashboard_layout));
  }, [prefs.dashboard_layout]);

  const persist = async (next: DashboardWidget[]): Promise<void> => {
    const prev = layout;
    setLayout(next);
    try {
      const result = await updatePreferences({ dashboard_layout: next });
      setUserPrefs(result);
    } catch {
      // toast surfaced by api wrapper — roll back so the screen doesn't show
      // a layout the server never saved (it would silently revert on reload).
      setLayout(prev);
    }
  };

  const onResize = (id: string, size: DashboardWidgetSize): void => {
    void persist(layout.map((w) => (w.id === id ? { ...w, size } : w)));
  };
  const onRemove = (id: string): void => {
    void persist(
      layout.filter((w) => w.id !== id).map((w, i) => ({ ...w, order: i + 1 }))
    );
  };
  const onAdd = (id: string): void => {
    const next: DashboardWidget = {
      id,
      size: "half",
      order: layout.length + 1,
    };
    void persist([...layout, next]);
    toastSuccess("Widget hinzugefügt");
    setLibraryOpen(false);
  };

  // Drag-Reorder (nur im Bearbeiten-Modus): Position via arrayMove tauschen,
  // order neu durchnummerieren, dann persistieren.
  const onDragStart = (e: DragStartEvent): void => {
    setActiveId(String(e.active.id));
  };
  const onDragEnd = (e: DragEndEvent): void => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = layout.findIndex((w) => w.id === active.id);
    const to = layout.findIndex((w) => w.id === over.id);
    if (from < 0 || to < 0) return;
    void persist(arrayMove(layout, from, to).map((w, i) => ({ ...w, order: i + 1 })));
  };

  const activeWidget = activeId
    ? layout.find((w) => w.id === activeId) ?? null
    : null;

  const availableForAdd = ALL_WIDGET_IDS.filter(
    (id) => !layout.some((w) => w.id === id)
  );

  const spans = gridSpans(layout);
  const firstName = me.user?.name.split(" ")[0] ?? "";
  const isGuest = me.user?.is_external === true;

  // Die zwei Kopf-Aktionen wandern unverändert in die Kopfzeile: „Widget" (nur
  // im Bearbeiten-Modus) und der Bearbeiten/Fertig-Umschalter. Vorher zwei,
  // nachher zwei — keine Aktion fällt weg.
  const headerActions = (
    <>
      {editMode && (
        <Button
          variant="secondary"
          size="lg"
          icon={Plus}
          onClick={() => setLibraryOpen(true)}
          disabled={availableForAdd.length === 0}
        >
          Widget
        </Button>
      )}
      <Button
        variant={editMode ? "primary" : "secondary"}
        size="lg"
        icon={editMode ? Check : Pencil}
        onClick={() => setEditMode((v) => !v)}
        aria-pressed={editMode}
      >
        {editMode ? "Fertig" : "Bearbeiten"}
      </Button>
    </>
  );

  // Unbedingt gerufen (Hook-Regel) — auch in den zwei Sonderfällen unten.
  // ACHTUNG Gast-Zweig: für externe Gäste rendert `Layout` KEINE App-Kopfzeile
  // (eigene schlanke Leiste ohne App-Navigation), dieser Wert bleibt dort also
  // ungelesen. Deshalb behält der Gast-Zweig seinen eigenen Kopf im Inhalt —
  // er wäre sonst titellos. Es gibt trotzdem nie zwei `h1`: entweder die
  // Kopfzeile (Normalfall) oder der Gast-Kopf (Gast-Zweig).
  usePageHeader({
    title: isGuest ? "Deine Projekte" : requireLabel("/dashboard"),
    subtitle: !me.user
      ? undefined
      : isGuest
        ? `Hallo ${firstName} — Projekte, zu denen du eingeladen wurdest.`
        : `Hallo ${firstName} — dein Überblick.`,
    actions: isGuest ? undefined : headerActions,
  });

  const renderCard = (
    widget: DashboardWidget,
    dragHandleProps?: Record<string, unknown>
  ): preact.JSX.Element => (
    <WidgetCard
      widget={widget}
      title={WIDGET_LABELS[widget.id as WidgetId] ?? widget.id}
      onResize={(s) => onResize(widget.id, s)}
      onRemove={() => onRemove(widget.id)}
      editing={editMode}
      dragHandleProps={dragHandleProps}
    >
      {widgetContent(widget.id)}
    </WidgetCard>
  );

  if (!me.user)
    return (
      <Layout>
        <PageShell>
          <p class="text-sm text-[color:var(--color-on-surface-variant)]">Lädt…</p>
        </PageShell>
      </Layout>
    );

  // Externe (projekt-skopierte) Gäste bekommen kein editierbares Widget-Grid —
  // nur die auf ihre eigenen Einladungen reduzierte Projektliste (Layout
  // routet task-skopierte Gäste ohnehin nie hierher).
  if (me.user.is_external)
    return (
      <Layout>
        <PageShell>
          {/* Kein `usePageHeader` sichtbar: der Gast-Zweig von `Layout` hat
              keine App-Kopfzeile — dieser Kopf ist hier die EINZIGE h1. */}
          <header class="mb-6">
            <h1 class="text-2xl font-bold text-[color:var(--color-on-surface)]">
              Deine Projekte
            </h1>
            <p class="text-sm text-[color:var(--color-on-surface-variant)] mt-0.5">
              Hallo {me.user.name.split(" ")[0]} — Projekte, zu denen du eingeladen wurdest.
            </p>
          </header>
          <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4">
            <MyProjectsWidget />
          </div>
        </PageShell>
      </Layout>
    );

  return (
    <Layout>
      <PageShell>
        {/* 12-Col-Grid auf md+ — die Spans kommen aus `gridSpans`, damit jede
            Reihe an derselben rechten Kante endet.
            Mobile stackt 1-spaltig (kein grid-column-span!), Tablet 2-spaltig
            via dedizierter CSS-Klasse `.juuplan-widget-wrap`.
            Im Bearbeiten-Modus: @dnd-kit zum Verschieben (Griff im
            Card-Header). Außerhalb: statisches Grid ohne DnD-Overhead. */}
        {editMode ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragCancel={() => setActiveId(null)}
          >
            <SortableContext
              items={layout.map((w) => w.id)}
              strategy={rectSortingStrategy}
            >
              <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4 auto-rows-min">
                {layout.map((widget, i) => (
                  <SortableWidget key={widget.id} widget={widget} span={spans[i]}>
                    {(dragHandleProps) => renderCard(widget, dragHandleProps)}
                  </SortableWidget>
                ))}
              </div>
            </SortableContext>
            {/* Schwebende Kopie: leichtgewichtiger Header-Ghost (kein Re-Mount
                der Widget-Inhalte → kein erneutes Laden/Fetch beim Ziehen). */}
            <DragOverlay>
              {activeWidget ? (
                <div class="rounded-lg border border-[color:var(--color-primary)] bg-[color:var(--color-surface-container-lowest)] shadow-level-3 px-4 py-2.5">
                  <div class="flex items-center gap-2">
                    <GripVertical
                      size={16}
                      class="text-[color:var(--color-on-surface-variant)]"
                      aria-hidden="true"
                    />
                    <span class="text-sm font-semibold text-[color:var(--color-on-surface)]">
                      {WIDGET_LABELS[activeWidget.id as WidgetId] ?? activeWidget.id}
                    </span>
                  </div>
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-4 auto-rows-min">
            {layout.map((widget, i) => (
              <div
                key={widget.id}
                class="juuplan-widget-wrap"
                style={{ "--juuplan-widget-span": spans[i] } as preact.JSX.CSSProperties}
              >
                {renderCard(widget)}
              </div>
            ))}
          </div>
        )}

        {layout.length === 0 && (
          <EmptyState
            icon={LayoutGrid}
            title="Dein Dashboard ist leer."
            description="Klick „Widget“ oben, um eines hinzuzufügen, oder lass dich von den Default-Widgets begleiten."
            action={
              <Button variant="primary" onClick={() => void persist(DEFAULT_LAYOUT)}>
                Standard-Layout laden
              </Button>
            }
          />
        )}
      </PageShell>

      {libraryOpen && (
        <WidgetLibrary
          onClose={() => setLibraryOpen(false)}
          onPick={onAdd}
          available={availableForAdd}
        />
      )}
    </Layout>
  );
}

function WidgetLibrary(props: {
  available: readonly string[];
  onPick: (id: string) => void;
  onClose: () => void;
}): preact.JSX.Element {
  // Eigenes `fixed inset-0`-Overlay ersetzt durch `components/Modal.tsx` —
  // dieselbe Abdunklung wie die restlichen Modal-Nutzer der App (das alte
  // `bg-black/40` war zudem eine Hex/Tailwind-Farbe ohne Token), plus
  // Fokus-Falle, Body-Scroll-Lock und Escape-Handling, die dieses Overlay
  // vorher selbst (und nur teilweise) nachbauen musste.
  return (
    <Modal
      title="Widget hinzufügen"
      onClose={props.onClose}
      size="sm"
      footer={
        <div class="flex justify-end">
          <Button variant="secondary" onClick={props.onClose}>
            Schließen
          </Button>
        </div>
      }
    >
      {props.available.length === 0 ? (
        <p class="text-sm text-[color:var(--color-on-surface-variant)]">
          Alle verfügbaren Widgets sind bereits auf deinem Dashboard.
        </p>
      ) : (
        <ul class="flex flex-col gap-1">
          {props.available.map((id) => (
            <li key={id}>
              <button
                type="button"
                onClick={() => props.onPick(id)}
                class="w-full text-left rounded px-3 py-2 text-sm hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
              >
                {WIDGET_LABELS[id as WidgetId] ?? id}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
